import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text
} from '@chakra-ui/react';
import { Notes } from '@codametrix/shared-views';
import { documentUtils, objectUtils } from '@codametrix/ui-common';
import * as commonApi from '@codametrix/client-api';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { NotesData } from '../stateMappers/caseRunToCodingScreenState';
import { noteKey } from './noteKey';
import { useNoteInteraction } from './noteInteractionContext';
import { cmxDocumentContentLabel } from './cmxDocumentContentLabel';

const RENDERABLE_VIA_NOTES = new Set([
  documentUtils.MimeTypes.HTML,
  documentUtils.MimeTypes.PDF
]);

const RENDITION_PRECEDENCE = [
  documentUtils.MimeTypes.HTML,
  documentUtils.MimeTypes.PDF,
  documentUtils.MimeTypes.TEXT
];

/** The single best rendition for a cmx-document-content note -- same precedence and grouping
 * `case-documents.actions.ts` already uses for legacy notes. */
function bestRendition(
  renditions: readonly CMxAPI.DocumentMetadata[] | undefined
): CMxAPI.DocumentMetadata | undefined {
  if (!renditions || renditions.length === 0) {
    return undefined;
  }
  const grouped = objectUtils.groupBy(renditions, 'mimeType');
  return documentUtils.firstAvailable(grouped, RENDITION_PRECEDENCE);
}

interface RenditionSourceState {
  readonly content: string | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
}

/**
 * Downloads the ORIGINAL rendition a cmx-document-content note resolved to (HTML preferred, then
 * PDF, then plain text -- see `bestRendition`), replacing the old `useCmxDocumentContentSource`
 * (RTF) read. Mirrors `use-cmx-document-content.ts`'s `useCmxDocumentContentSource` stale-request
 * guard: a doc change while a request is in flight must not let the stale response win.
 */
function useRenditionSource(
  doc: CMxAPI.DocumentMetadata | undefined
): RenditionSourceState {
  const [content, setContent] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const currentRequest = useRef(0);

  const load = useCallback(() => {
    if (!doc) {
      setContent(undefined);
      setError(null);
      setLoading(false);
      return;
    }
    currentRequest.current += 1;
    const requestId = currentRequest.current;
    const isCurrent = () => currentRequest.current === requestId;
    const isPdf = doc.mimeType === documentUtils.MimeTypes.PDF;
    const endpoint = `/documents/download/${doc.documentId}${
      isPdf ? '/encoded' : ''
    }/v1`;

    setLoading(true);
    setError(null);

    commonApi
      .request(endpoint)
      .then(async (response) => {
        const text = await response.text();
        if (isCurrent()) {
          setContent(text);
        }
      })
      .catch((caught: unknown) => {
        if (isCurrent()) {
          setContent(undefined);
          setError(caught instanceof Error ? caught : new Error(String(caught)));
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setLoading(false);
        }
      });
  }, [doc]);

  useEffect(() => {
    load();
    return () => {
      currentRequest.current += 1;
    };
  }, [load]);

  return { content, loading, error };
}

export interface SourceDocumentModalProps {
  readonly notes: NotesData['notes'];
  readonly cmxDocumentContents: NotesData['cmxDocumentContents'];
}

export const SourceDocumentModal: React.FC<SourceDocumentModalProps> = ({
  notes,
  cmxDocumentContents
}) => {
  const { sourceModalNoteKey, closeSourceModal } = useNoteInteraction();

  const cmxDocumentContentIndex = cmxDocumentContents.findIndex(
    (entry) => entry.documentId === sourceModalNoteKey
  );
  const cmxDocumentContentEntry =
    cmxDocumentContentIndex === -1 ? undefined : cmxDocumentContents[cmxDocumentContentIndex];
  const isCmxDocumentContent = cmxDocumentContentEntry !== undefined;

  const rendition = bestRendition(cmxDocumentContentEntry?.renditions);
  const {
    content: renditionContent,
    loading: renditionLoading,
    error: renditionError
  } = useRenditionSource(rendition);

  const note = isCmxDocumentContent
    ? null
    : notes.find((n) => noteKey(n) === sourceModalNoteKey) ?? null;

  const label = isCmxDocumentContent
    ? cmxDocumentContentLabel(cmxDocumentContentEntry, cmxDocumentContentIndex)
    : undefined;

  const syntheticNote =
    rendition && renditionContent !== undefined && RENDERABLE_VIA_NOTES.has(rendition.mimeType)
      ? documentUtils.decoratedNoteFactory({
          content: renditionContent,
          filetype: rendition.mimeType,
          filename: label
        })
      : null;

  const isOpen = isCmxDocumentContent
    ? sourceModalNoteKey !== undefined
    : sourceModalNoteKey !== undefined && note !== null;

  return (
    <Modal isOpen={isOpen} onClose={closeSourceModal} size="4xl">
      <ModalOverlay />
      <ModalContent data-testid="source-document-modal">
        <ModalHeader>
          {isCmxDocumentContent ? label : note?.filename ?? 'Document'}
        </ModalHeader>
        <ModalCloseButton aria-label="Close source document" />
        <ModalBody>
          {isCmxDocumentContent ? (
            renditionLoading ? (
              <Flex align="center" justify="center" p={4} gap={3}>
                <Spinner size="sm" />
                <Text color="gray.500">Loading original document…</Text>
              </Flex>
            ) : renditionError ? (
              <Flex align="center" justify="center" p={4}>
                <Text color="red.600">
                  Could not load the original document: {renditionError.message}
                </Text>
              </Flex>
            ) : syntheticNote ? (
              <Notes notes={[syntheticNote]} disabled />
            ) : (
              <Box
                as="pre"
                p={4}
                whiteSpace="pre-wrap"
                sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              >
                {renditionContent ?? ''}
              </Box>
            )
          ) : (
            <Notes notes={note ? [note] : []} disabled />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
